/**
 * A fill-level bar: a track with the used share drawn over it, in the colour of how full it is.
 *
 * The monitor page's volume rows. A percentage in text alone buries the one volume that is nearly
 * full, and Webwin's `ProgressBar` takes its colour from the theme's accent, which cannot say "this
 * one is at 92%". Coloured from the palette in `onPaint`, so it follows a theme switch.
 */
import { Control, type ControlOptions, type RenderContext, type Theme } from 'webwin';
import { crm } from '../theme/CrmTheme.js';
import type { ChipTone } from './Chip.js';

export interface UsageBarOptions extends ControlOptions {
  percent?: number;
  tone?: ChipTone;
}

export class UsageBar extends Control {
  percent: number;
  tone: ChipTone;

  constructor(options: UsageBarOptions = {}) {
    super({ height: 8, hAlign: 'stretch', ...options });
    this.percent = options.percent ?? 0;
    this.tone = options.tone ?? 'ok';
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const c = crm(theme);
    const r = this.localRect;
    rc.drawRect(r, { fill: c.altSurface, radius: r.height / 2, borderWidth: 1, borderColor: c.border });
    const share = Math.max(0, Math.min(100, this.percent)) / 100;
    if (share <= 0) return;
    // At least a sliver, so a nearly empty volume still reads as a bar with something in it.
    const width = Math.max(r.height, r.width * share);
    const fill = this.tone === 'danger' ? c.danger : this.tone === 'warn' ? c.warn : c.ok;
    rc.drawRect({ x: r.x, y: r.y, width, height: r.height }, { fill, radius: r.height / 2 });
  }
}
